import { apiService } from "./apiService";
import { getAccessToken } from "./authService";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000/api";

function authHeaders() {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listDisconnectRequests(tenantId, options = {}) {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);
  if (options.tab) params.append("tab", options.tab);
  if (options.status) params.append("status", options.status);
  if (options.provider) params.append("provider", options.provider);
  if (options.reason) params.append("reason", options.reason);
  if (options.search) params.append("search", options.search);
  if (options.page) params.append("page", String(options.page));
  if (options.limit) params.append("limit", String(options.limit));
  return apiService.request(`/disconnects?${params.toString()}`);
}

export async function getDisconnectRequest(id, tenantId) {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);
  return apiService.request(`/disconnects/${id}?${params.toString()}`);
}

export async function getDisconnectByCircuit(tenantId, { inventoryId, circuitId } = {}) {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);
  if (inventoryId != null) params.append("inventory_id", String(inventoryId));
  if (circuitId) params.append("circuit_id", circuitId);
  return apiService.request(`/disconnects/by-circuit?${params.toString()}`);
}

export async function listProviderCircuits(tenantId, provider, options = {}) {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);
  if (provider) params.append("provider", provider);
  if (options.search) params.append("search", options.search);
  if (options.excludeRequestId) {
    params.append("exclude_request_id", String(options.excludeRequestId));
  }
  return apiService.request(
    `/disconnects/circuits-for-provider?${params.toString()}`
  );
}

/**
 * Create disconnect request. `payload` is plain fields; `files` is File[].
 * Groups are serialized as JSON in FormData when files are present.
 */
export async function createDisconnectRequest(payload, files = []) {
  const formData = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value == null) return;
    if (typeof value === "object") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
  (files || []).forEach((file) => {
    if (file) formData.append("evidence", file);
  });

  const response = await fetch(`${API_BASE_URL}/disconnects`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.message || data.error || "Failed to submit disconnect");
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function updateDisconnectRequest(id, payload, files = []) {
  const formData = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value == null) return;
    if (typeof value === "object") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
  (files || []).forEach((file) => {
    if (file) formData.append("evidence", file);
  });

  const response = await fetch(`${API_BASE_URL}/disconnects/${id}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.message || data.error || "Failed to update disconnect");
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

export function evidenceFileUrl(requestId, evidenceId, tenantId) {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);
  const token = getAccessToken();
  // Browser can't set Authorization on window.open easily; use query token only if API supports it.
  // Prefer fetch+blob in UI; this URL is for authenticated fetch.
  return `${API_BASE_URL}/disconnects/${requestId}/evidence/${evidenceId}/file?${params.toString()}${
    token ? "" : ""
  }`;
}

export async function fetchEvidenceBlob(requestId, evidenceId, tenantId) {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);
  const response = await fetch(
    `${API_BASE_URL}/disconnects/${requestId}/evidence/${evidenceId}/file?${params.toString()}`,
    { headers: authHeaders() }
  );
  if (!response.ok) throw new Error("Unable to open evidence file");
  return response.blob();
}

export async function deleteEvidence(requestId, evidenceId, tenantId) {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);
  return apiService.request(
    `/disconnects/${requestId}/evidence/${evidenceId}?${params.toString()}`,
    { method: "DELETE" }
  );
}

export const DISCONNECT_REASONS = [
  "Site closed",
  "Moved to new provider",
  "Duplicate service",
  "No longer needed",
  "Cost reduction",
  "Other",
];

export const DISCONNECT_STATUS = {
  SUBMITTED: "submitted",
  CONFIRMED: "confirmed_by_supplier",
  BILLING_STOPPED: "billing_stopped",
};

export function statusLabel(status) {
  switch (status) {
    case DISCONNECT_STATUS.CONFIRMED:
      return "Confirmed by supplier";
    case DISCONNECT_STATUS.BILLING_STOPPED:
      return "Billing stopped";
    case DISCONNECT_STATUS.SUBMITTED:
    default:
      return "Submitted";
  }
}

export function statusStepIndex(status) {
  if (status === DISCONNECT_STATUS.BILLING_STOPPED) return 3;
  if (status === DISCONNECT_STATUS.CONFIRMED) return 2;
  return 1;
}
